import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../utils/client';
import { useCaps } from '../contexts/CapabilitiesContext';

type CurrencyCode = 'GBP' | 'EUR' | 'USD' | 'CHF';

interface CreditPricingTier {
  id: number;
  currency_code: CurrencyCode;
  min_credits: number;
  max_credits: number;
  discount_percent: string;
  cost_per_credit: string;
  display_order: number;
}

interface PurchaseCreditsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onEnquireEnterprise: () => void;
  selectedCurrency?: string;
}

const CURRENCIES: Array<{ code: CurrencyCode; label: string; symbol: string }> = [
  { code: 'GBP', label: 'GBP £', symbol: '£' },
  { code: 'EUR', label: 'EUR €', symbol: '€' },
  { code: 'USD', label: 'USD $', symbol: '$' },
  { code: 'CHF', label: 'CHF Fr', symbol: 'Fr' },
];

const MAX_TIER_CREDITS = 3999;

function isSupportedCurrency(value: string | undefined): value is CurrencyCode {
  return CURRENCIES.some((currency) => currency.code === value);
}

function formatCreditRange(min: number, max: number): string {
  return `${min.toLocaleString('en-US')} – ${max.toLocaleString('en-US')}`;
}

function formatDiscount(value: string): string {
  return `${Number(value).toFixed(2)}%`;
}

function formatCostPerCredit(value: string): string {
  return Number(value).toFixed(1);
}

function formatTotal(symbol: string, total: number): string {
  if (total <= 0) return `${symbol}0`;
  return `${symbol}${total.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({
  open,
  onClose,
  onSuccess,
  onEnquireEnterprise,
  selectedCurrency = 'CHF',
}) => {
  const { can } = useCaps();
  const canPurchaseCredits = can('marketplace_purchase_credits');
  const [tiers, setTiers] = useState<CreditPricingTier[]>([]);
  const [currency, setCurrency] = useState<CurrencyCode>(
    isSupportedCurrency(selectedCurrency) ? selectedCurrency : 'CHF'
  );
  const [creditsInput, setCreditsInput] = useState('0');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setCurrency(isSupportedCurrency(selectedCurrency) ? selectedCurrency : 'CHF');
    setCreditsInput('0');
    setIsSubmitting(false);
    setError(null);

    let cancelled = false;
    setIsLoading(true);
    api<CreditPricingTier[] | { results: CreditPricingTier[] }>('/api/credit-pricing-tiers/')
      .then((data) => {
        if (!cancelled) {
          setTiers(Array.isArray(data) ? data : data?.results ?? []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load pricing');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selectedCurrency]);

  const credits = useMemo(() => {
    const parsed = Number.parseInt(creditsInput.replace(/[^\d]/g, ''), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [creditsInput]);

  const currencyTiers = useMemo(
    () =>
      tiers
        .filter((tier) => tier.currency_code === currency)
        .sort((a, b) => a.display_order - b.display_order || a.min_credits - b.min_credits),
    [tiers, currency]
  );

  const activeTier = useMemo(
    () =>
      currencyTiers.find(
        (tier) => credits >= tier.min_credits && credits <= tier.max_credits
      ) ?? null,
    [currencyTiers, credits]
  );

  const currencyMeta = CURRENCIES.find((item) => item.code === currency) ?? CURRENCIES[3];
  const costPerCredit = activeTier ? Number(activeTier.cost_per_credit) : 0;
  const total = credits > 0 && activeTier ? credits * costPerCredit : 0;
  const isEnterpriseVolume = credits > MAX_TIER_CREDITS;
  const canSubmit =
    canPurchaseCredits &&
    credits >= 1 &&
    Boolean(activeTier) &&
    !isSubmitting &&
    !isLoading;

  const handleCreditsChange = (value: string) => {
    const digits = value.replace(/[^\d]/g, '');
    setCreditsInput(digits === '' ? '' : String(Number.parseInt(digits, 10)));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    setError(null);
    try {
      await api('/api/credit-purchase-requests/', {
        method: 'POST',
        body: JSON.stringify({
          credits,
          currency_code: currency,
        }),
      });
      onSuccess();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send request';
      setError(message);
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="purchase-credits-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[920px] rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-1 text-[#4A4A4A] hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.45fr)]">
          <div className="flex flex-col px-8 pt-8 pb-6 md:pr-10 md:border-r md:border-[#E8E8E8]">
            <h2
              id="purchase-credits-title"
              className="text-[22px] leading-7 font-bold text-[#0C0B0B] pr-8"
            >
              How many credits would you like to purchase?
            </h2>
            <p className="mt-2 text-[14px] text-[#8A8A8A]">Calculate your credit cost</p>

            <div className="mt-8 flex items-center gap-3">
              <input
                id="purchase-credits-amount"
                type="text"
                inputMode="numeric"
                value={creditsInput}
                onChange={(event) => handleCreditsChange(event.target.value)}
                className="w-[88px] h-10 rounded-md border border-[#E0E0E0] bg-[#F7F7F7] px-3 text-[16px] text-[#0C0B0B] text-center focus:outline-none focus:ring-1 focus:ring-[#674fa3]"
                aria-label="Number of credits"
              />
              <label htmlFor="purchase-credits-amount" className="text-[16px] text-[#0C0B0B]">
                credits
              </label>
            </div>

            <div className="mt-10">
              <p className="text-[40px] leading-none font-bold text-[#0C0B0B] tracking-tight">
                {formatTotal(currencyMeta.symbol, total)}
              </p>
              <p className="mt-2 text-[14px] text-[#0C0B0B]">one time</p>
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              title={
                canPurchaseCredits
                  ? undefined
                  : 'You do not have permission to purchase credits'
              }
              className={`mt-8 self-start px-6 py-2.5 rounded-full text-[14px] font-medium transition-colors ${
                canSubmit
                  ? 'bg-[#674fa3] text-white hover:bg-[#5a4490]'
                  : 'bg-[#674fa3]/40 text-white cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Sending…' : 'Request credits'}
            </button>

            {!canPurchaseCredits && (
              <p className="mt-3 text-sm text-[#626262]">
                You do not have permission to purchase credits.
              </p>
            )}

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
            {isEnterpriseVolume && (
              <p className="mt-3 text-sm text-[#626262]">
                Need more than 3,999 credits? Enquire about enterprise partnership pricing.
              </p>
            )}

            <p className="mt-auto pt-10 text-[12px] leading-5 text-[#8A8A8A]">
              Credits are valid for 12 months from purchase, or can be aligned to your fiscal year.
            </p>
          </div>

          <div className="flex flex-col px-8 pt-8 pb-6 md:pl-10">
            <div className="flex justify-end pr-6">
              <div className="inline-flex items-center rounded-full bg-[#F3F3F3] p-1">
                {CURRENCIES.map((item) => {
                  const isActive = item.code === currency;
                  return (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => setCurrency(item.code)}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                        isActive
                          ? 'bg-black text-white'
                          : 'text-[#4A4A4A] hover:text-[#0C0B0B]'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-6">
              <div className="grid grid-cols-[1.2fr_0.9fr_1fr] gap-3 px-3 pb-2 text-[13px] font-semibold text-[#0C0B0B]">
                <span>Credits</span>
                <span>Discount</span>
                <span className="text-right">Cost per credit</span>
              </div>
              {isLoading ? (
                <p className="px-3 py-6 text-sm text-[#8A8A8A]">Loading pricing…</p>
              ) : (
                <div>
                  {currencyTiers.map((tier) => {
                    const isActive = activeTier?.id === tier.id;
                    return (
                      <div
                        key={tier.id}
                        className={`grid grid-cols-[1.2fr_0.9fr_1fr] gap-3 px-3 py-2.5 text-[13px] rounded-md ${
                          isActive
                            ? 'bg-[#F3EEF8] text-[#674fa3] font-medium'
                            : 'text-[#4A4A4A]'
                        }`}
                      >
                        <span>{formatCreditRange(tier.min_credits, tier.max_credits)}</span>
                        <span>{formatDiscount(tier.discount_percent)}</span>
                        <span className="text-right">{formatCostPerCredit(tier.cost_per_credit)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <p className="mt-6 text-[13px] text-[#4A4A4A]">
              Need more than 3,999 credits?{' '}
              <button
                type="button"
                onClick={onEnquireEnterprise}
                className="font-semibold text-[#674fa3] hover:text-[#5a4490]"
              >
                Enquire about enterprise partnership pricing
              </button>
            </p>
            <p className="mt-3 text-[12px] leading-5 text-[#8A8A8A]">
              Multi-year commitments unlock further discounts, free workshops and meta-analysis.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseCreditsModal;
